import { I18N_DEFAULT_LANGUAGE } from '@infra/i18n';
import { environment } from '@shared/config';

/**
 * Dates and times as the app shows them to a person.
 *
 * The helper is handed a language and nothing else: it never asks which
 * language, so a new one needs no change here. Each style names one set of
 * fields — day, abbreviated month, year — and `Intl` orders and words them for
 * whatever locale it was given.
 *
 * Everything is rendered in the app's own configured timezone
 * (`VITE_APP_TIMEZONE`), so a row and the chart beside it always agree on which
 * day they belong to.
 *
 * Lives here, not in `@shared/lib`, because it constructs `Intl.DateTimeFormat`:
 * `src/infra/format` is the one place allowed to, and the lint rule enforcing that
 * is what moved it. `@shared/lib` still re-exports it, so call sites are unchanged.
 *
 * @module @infra/format
 */

/** `full` carries the year; `short` drops it for dense lists; `month` is a column header. */
export type AppDateStyle = 'full' | 'short' | 'month';
/** `hm` is the usual clock; `hms` is for a start time read against a duration. */
export type AppTimeStyle = 'hm' | 'hms';

const DATE_FIELDS: Record<AppDateStyle, Intl.DateTimeFormatOptions> = {
  full: { day: 'numeric', month: 'short', year: 'numeric' },
  short: { day: 'numeric', month: 'short' },
  month: { month: 'short' },
};

const TIME_FIELDS: Record<AppTimeStyle, Intl.DateTimeFormatOptions> = {
  hm: { hour: '2-digit', minute: '2-digit' },
  hms: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
};

/** A bare calendar day, as the API sends heat-map and activity rows. */
const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A well-formed but unknown zone passes env validation's pattern and would then
 * throw out of every render, so it is checked once and downgraded to the
 * viewer's zone rather than taking the page down.
 */
const APP_TIME_ZONE = (() => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: environment.appTimezone });
    return environment.appTimezone;
  } catch {
    console.error(
      `Unknown VITE_APP_TIMEZONE "${environment.appTimezone}" — falling back to the browser timezone.`
    );
    return undefined;
  }
})();

function render(
  value: string | number | Date,
  language: string,
  fields: Intl.DateTimeFormatOptions
): string {
  // A `YYYY-MM-DD` string names a day, not an instant: it parses as UTC
  // midnight, and converting it into another zone can hand back the day before.
  const isPlainDate = typeof value === 'string' && PLAIN_DATE.test(value);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Never `undefined`: that would hand the choice to the viewer's browser, which
  // is the drift this helper exists to remove.
  return new Intl.DateTimeFormat(language || I18N_DEFAULT_LANGUAGE, {
    ...fields,
    timeZone: isPlainDate ? 'UTC' : APP_TIME_ZONE,
  }).format(date);
}

export function formatAppDate(
  value: string | number | Date,
  language: string,
  style: AppDateStyle = 'full'
): string {
  return render(value, language, DATE_FIELDS[style]);
}

export function formatAppTime(
  value: string | number | Date,
  language: string,
  style: AppTimeStyle = 'hm'
): string {
  return render(value, language, TIME_FIELDS[style]);
}
